/**
 * `SCR-PHOTOSET` — P-PHOTO-01, at the screen. OWNER: web-frontend-engineer.
 *
 * LAW 31: "the manufacturer sees pictures of the customer also because paul
 * takes 3 pictures". OWNER RULING OR-2: they decide the shoulder treatment, the
 * stance and the balance of the coat — a construction input, not a courtesy.
 *
 * ── THE CONSENT IS THE FIRST THING ON THE SCREEN, AND THAT IS THE POINT ────
 *
 * OR-4 kept exactly one thing from the privacy build: consent taken ONCE, in
 * plain words, at the appointment. OR-1 requires those words to name where the
 * photographs go, because that is what makes the client's own consent the
 * lawful basis for sending them to Thailand.
 *
 * So the sentence is a TEXT FIELD, not a tick box, and it is above the camera
 * rather than beside the save button. A tailor filling this in is writing down
 * what he just said out loud, in his own words — and the server stores it
 * verbatim and refuses to keep a photograph without it. Law 32(d): the
 * mechanism is ours, the sentence is his.
 *
 * ── THE CAMERA ────────────────────────────────────────────────────────────
 *
 * `<input type="file" accept="image/*" capture="environment">`. On an iPad that
 * is the camera; on a laptop it is the file picker; and it is the same element
 * either way, which is why there is no branch on the device anywhere in here.
 * The bytes are read to base64 and sent whole — the server sniffs what they
 * actually are and refuses anything that is not a JPEG, a PNG or a WebP,
 * whatever the file was called.
 */

import { useRef, useState } from 'react';
import { t } from '@pt/copy';
import { dateInWords } from '@pt/view';
import { api } from '../../lib/api.ts';
import { useResource, useSubmission } from '../../lib/resource.ts';
import type { PhotographSet } from '../../lib/contracts.ts';
import { AppShell } from '../../app/AppShell.tsx';
import { useNavigation } from '../../app/Router.tsx';
import { Button, FactLine, Statement, TextInput } from '../../components/primitives.tsx';
import { RefusalStatement } from '../../components/backoffice.tsx';
import { FetchError, LoadingRegion, OfflineStatement } from '../../components/states.tsx';

interface Taken {
  readonly pose: string;
  readonly dataBase64: string;
  readonly previewUrl: string;
  readonly fileName: string;
}

/** The bytes, as the server wants them. No canvas, no re-encode: what the camera gave us. */
function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('unreadable'));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma === -1 ? result : result.slice(comma + 1));
    };
    reader.readAsDataURL(file);
  });
}

export function PhotographSetScreen({ clientId }: { clientId: string }) {
  const { navigate } = useNavigation();
  const submission = useSubmission();
  const detail = useResource(() => api.atelierClient(clientId), [clientId]);
  const sets = useResource(() => api.photographSets(clientId), [clientId]);

  const [consent, setConsent] = useState('');
  const [place, setPlace] = useState('');
  const [note, setNote] = useState('');
  const [taken, setTaken] = useState<readonly Taken[]>([]);
  const [saved, setSaved] = useState<PhotographSet | null>(null);
  const key = useRef(`photoset-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

  const poses = sets.data?.housePoses ?? ['front', 'side', 'back'];
  const existing = (sets.data?.sets ?? []).find((set) => set.governs) ?? null;
  const clientName = detail.data?.client.displayName ?? '';

  const take = async (pose: string, file: File | null) => {
    if (file === null) return;
    const dataBase64 = await readAsBase64(file);
    setTaken((current) => [
      ...current.filter((entry) => entry.pose !== pose),
      { pose, dataBase64, previewUrl: URL.createObjectURL(file), fileName: file.name },
    ]);
  };

  const submit = () =>
    void submission.run(async () => {
      const written = await api.createPhotographSet(clientId, {
        ...(place.trim() === '' ? {} : { takenAtPlace: place.trim() }),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
        ...(consent.trim() === ''
          ? {}
          : { consent: { statementText: consent.trim(), statementVersion: 1 } }),
        photographs: [...taken]
          .sort((a, b) => poses.indexOf(a.pose) - poses.indexOf(b.pose))
          .map((entry) => ({ pose: entry.pose, dataBase64: entry.dataBase64 })),
        idempotencyKey: key.current,
      });
      setSaved(written.set);
      return written;
    });

  if (saved !== null) {
    return (
      <AppShell title={t('photoset.heading', { clientNameStaff: clientName })}>
        <Statement>
          {saved.photographs.length === 1
            ? t('photoset.done.one')
            : t('photoset.done.many', { count: String(saved.photographs.length) })}
        </Statement>
        <FactLine>{saved.measurementNote}</FactLine>
        <div className="pt-actions">
          <Button onClick={() => navigate(`/atelier/clients/${clientId}`)}>
            {t('clientnew.open.action')}
          </Button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title={t('photoset.heading', { clientNameStaff: clientName })}>
      {detail.phase === 'skeleton' || detail.phase === 'idle' ? (
        <LoadingRegion rows={6} variant="block" slow={detail.slow} label={t('client.photographs.heading')} />
      ) : null}
      {detail.phase === 'error' ? <FetchError onRetry={detail.reload} /> : null}

      {detail.phase !== 'ready' ? null : (
        <>
          <Statement>{t('photoset.lede')}</Statement>
          {existing === null ? null : (
            <FactLine>
              {`${t('photoset.replaces')} ${t('client.photographs.taken', {
                at: dateInWords(existing.takenAt),
                authorName: existing.takenByName,
              })}`}
            </FactLine>
          )}

          {/* ── the sentence, before the camera ────────────────────────── */}
          <section className="pt-block">
            <h2 className="pt-cutsheet__blockTitle">{t('photoset.consent.heading')}</h2>
            <Statement>{t('photoset.consent.lede')}</Statement>
            <TextInput
              id="pt-photoset-consent"
              label={t('photoset.consent.label')}
              value={consent}
              onChange={setConsent}
              multiline
              autoComplete="off"
            />
            {existing === null ? null : <FactLine>{t('photoset.consent.onfile')}</FactLine>}
          </section>

          {/* ── the three ─────────────────────────────────────────────── */}
          <section className="pt-block">
            <h2 className="pt-cutsheet__blockTitle">{t('client.photographs.heading')}</h2>
            <ul>
              {poses.map((pose) => {
                const held = taken.find((entry) => entry.pose === pose) ?? null;
                return (
                  <li key={pose} className="pt-row">
                    <div className="pt-input">
                      <label className="pt-input__label" htmlFor={`pt-photoset-${pose}`}>
                        {`${t('photoset.file.label')} · ${t('client.photographs.pose', { poseName: pose })}`}
                      </label>
                      <input
                        id={`pt-photoset-${pose}`}
                        className="pt-input__field"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        capture="environment"
                        onChange={(event) => void take(pose, event.target.files?.[0] ?? null)}
                      />
                    </div>
                    {held === null ? null : (
                      <img
                        className="pt-photoset__preview"
                        src={held.previewUrl}
                        alt={t('client.photographs.pose', { poseName: pose })}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
            {taken.length === 0 ? <Statement>{t('photoset.empty')}</Statement> : null}
          </section>

          <TextInput
            id="pt-photoset-place"
            label={t('photoset.place.label')}
            value={place}
            onChange={setPlace}
            autoComplete="off"
          />
          <TextInput
            id="pt-photoset-note"
            label={t('photoset.note.label')}
            value={note}
            onChange={setNote}
            multiline
            autoComplete="off"
          />

          {submission.rejection === null ? null : (
            <RefusalStatement rejection={submission.rejection} />
          )}
          {submission.offline ? <OfflineStatement /> : null}

          <div className="pt-actions">
            <Button onClick={submit} busy={submission.busy}>
              {t('photoset.submit')}
            </Button>
          </div>
        </>
      )}
    </AppShell>
  );
}
